<template>
  <div class="content disparo-rpa" data-testid="disparo-rpa-page">
    <div class="header d-flex justify-content-between align-items-center p-2">
      <div class="title">
        <h3 class="h3 mb-n2 bold">Disparo RPA</h3>
        <span class="subtitle-2 grey--text text--darken-1">
          Dados consolidados que serao enviados para o RPA
        </span>
      </div>

      <div class="actions d-flex flex-row align-center">
        <!-- <v-btn color="primary" large depressed @click="doConfirmIntegration()" :loading="loading">
          <v-icon left>mdi-robot-outline</v-icon>
          Disparar Integração RPA
        </v-btn> -->

        <v-btn
          class="elevation-4 mr-2"
          :loading="loading"
          data-testid="disparo-rpa-refresh"
          @click="doLoad"
        >
          <v-icon left dark>mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>
      </div>
    </div>

    <div class="d-flex align-center flex-wrap mt-2 mb-2">
      <year-select style="max-width: 150px" class="mr-3" data-testid="disparo-rpa-ano-base" />
      <v-chip-group
        v-model="filtroTipos"
        multiple
        class="mr-3"
        data-testid="disparo-rpa-filtro-tipo"
      >
        <v-chip
          v-for="(label, tipo) in tipoLabels"
          :key="tipo"
          :value="tipo"
          filter
          outlined
          small
          :data-testid="`disparo-rpa-filtro-tipo-${tipo}`"
        >
          {{ label }}
        </v-chip>
      </v-chip-group>
      <v-autocomplete
        v-model="filtroProjeto"
        :items="projetosDisponiveis"
        clearable
        dense
        outlined
        hide-details
        label="Projeto"
        style="max-width: 320px"
        class="mr-3"
        data-testid="disparo-rpa-filtro-projeto"
      />
      <v-text-field
        v-model="busca"
        clearable
        dense
        outlined
        hide-details
        prepend-inner-icon="mdi-magnify"
        label="Buscar item"
        style="max-width: 280px"
        class="mr-3"
        data-testid="disparo-rpa-busca"
      />
      <v-btn
        v-if="filtrosAtivos"
        text
        small
        data-testid="disparo-rpa-limpar-filtros"
        @click="limparFiltros"
      >
        <v-icon left small>mdi-filter-remove-outline</v-icon>
        Limpar filtros
      </v-btn>
      <v-spacer />
    </div>

    <div class="body">
      <v-progress-linear v-if="loading" indeterminate color="#1c1c44" />

      <template v-if="!loading">
        <v-row dense class="mb-3">
          <v-col v-for="card in summaryCards" :key="card.key" cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 text-center summary-card">
              <v-icon :color="card.color" class="mb-1">{{ card.icon }}</v-icon>
              <div class="text-h5 font-weight-bold" :style="{ color: card.color }">
                {{ card.value }}
              </div>
              <div class="caption grey--text text--darken-1">{{ card.label }}</div>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="!hasData">
          <div class="text-center grey--text pa-6">Nenhum registro encontrado</div>
        </template>

        <template v-else>
          <v-card class="mb-4 elevation-2" outlined>
            <v-card-title class="py-2 px-4 section-title">
              <span class="subtitle-1 font-weight-bold">Quantitativo</span>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-4">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Tipo</th>
                      <th class="text-right">Projetos</th>
                      <th class="text-right">Itens</th>
                      <th class="text-right">Horas</th>
                      <th class="text-right">Quantidade</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in quantitativoRows" :key="row.tipo">
                      <td>{{ row.label }}</td>
                      <td class="text-right">{{ row.projetos }}</td>
                      <td class="text-right">{{ row.itens }}</td>
                      <td class="text-right">{{ formatNumber(row.horas) }}</td>
                      <td class="text-right">{{ formatNumber(row.quantidade) }}</td>
                      <td class="text-right">{{ formatMoney(row.valor) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <div
            v-if="filtrosAtivos"
            class="caption grey--text mb-2"
            data-testid="disparo-rpa-resultado-info"
          >
            Exibindo {{ gruposFiltrados.length }} de {{ qualitativoRows.length }} grupos
            ({{ totalItensFiltrados }} itens)
          </div>

          <div
            v-if="filtrosAtivos && !gruposFiltrados.length"
            class="text-center grey--text pa-6"
            data-testid="disparo-rpa-sem-resultados"
          >
            Nenhum grupo corresponde aos filtros.
            <a @click="limparFiltros">Limpar filtros</a>
          </div>

          <v-expansion-panels
            v-model="paineisAbertos"
            multiple
            data-testid="disparo-rpa-grupos"
          >
            <v-expansion-panel
              v-for="(grupo, index) in gruposFiltrados"
              :key="`${grupo.tipo}-${projectKey(grupo.projeto)}`"
              :data-testid="`disparo-rpa-grupo-${index}`"
            >
              <v-expansion-panel-header class="section-title py-2 px-4">
                <span class="subtitle-1 font-weight-bold flex-grow-0 mr-2">
                  {{ grupo.label }} - {{ projectName(grupo.projeto) }}
                </span>
                <v-spacer />
                <v-chip x-small class="flex-grow-0 mr-2" color="#1c1c44" text-color="white">
                  {{ grupo.items.length }} item(ns)
                </v-chip>
                <span class="flex-grow-0 mr-4 font-weight-bold">
                  {{ formatMoney(grupo.valorTotal) }}
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Identificacao</th>
                        <th class="text-left">Funcao/Tipo</th>
                        <th class="text-left">Justificativa</th>
                        <th class="text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, itemIndex) in grupo.items"
                        :key="`${grupo.tipo}-${index}-${itemIndex}`"
                      >
                        <td>{{ item.identificacao || "-" }}</td>
                        <td>{{ item.funcao_projeto || item.tipo || item.tipo_rh || "-" }}</td>
                        <td>{{ item.justificativa || item.agrupamento_justificativa || "-" }}</td>
                        <td class="text-right">{{ formatMoney(item.valor) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import rdaRpaService from "@/api/rda/rdaRpa.service";

const tipoLabels = {
  colaboradores: "Colaboradores",
  terceiros: "Servicos terceiros",
  materiais: "Materiais",
};

export default {
  components: {
    YearSelect: () => import("@/components/year-select.vue"),
  },
  data() {
    return {
      loading: false,
      tipoLabels,
      filtroTipos: [],
      filtroProjeto: null,
      busca: "",
      paineisAbertos: [],
      consolidado: {
        quantitativo: {},
        qualitativo: [],
      },
    };
  },
  computed: {
    ...mapGetters(["clientId", "anoBase", "selectedClient"]),
    quantitativoRows() {
      return Object.entries(this.consolidado.quantitativo || {}).map(([tipo, row]) => ({
        tipo,
        label: tipoLabels[tipo] || tipo,
        projetos: row.projetos || 0,
        itens: row.itens || 0,
        horas: row.horas || 0,
        quantidade: row.quantidade || 0,
        valor: row.valor || 0,
      }));
    },
    qualitativoRows() {
      return this.consolidado.qualitativo || [];
    },
    hasData() {
      return this.quantitativoRows.some((row) => row.itens > 0) || this.qualitativoRows.length > 0;
    },
    projetosDisponiveis() {
      const projetos = new Map();
      this.qualitativoRows.forEach((grupo) => {
        const key = this.projectKey(grupo.projeto);
        if (!projetos.has(key)) {
          projetos.set(key, { value: key, text: this.projectName(grupo.projeto) });
        }
      });
      return Array.from(projetos.values()).sort((a, b) => a.text.localeCompare(b.text));
    },
    filtrosAtivos() {
      return this.filtroTipos.length > 0 || !!this.filtroProjeto || !!(this.busca || "").trim();
    },
    gruposFiltrados() {
      const busca = this.normalize((this.busca || "").trim());
      return this.qualitativoRows
        .filter((grupo) => !this.filtroTipos.length || this.filtroTipos.includes(grupo.tipo))
        .filter((grupo) => !this.filtroProjeto || this.projectKey(grupo.projeto) === this.filtroProjeto)
        .map((grupo) => {
          const items = !busca
            ? grupo.items || []
            : (grupo.items || []).filter((item) =>
                [
                  item.identificacao,
                  item.justificativa,
                  item.agrupamento_justificativa,
                  item.funcao_projeto,
                  item.tipo,
                  item.tipo_rh,
                ].some((campo) => this.normalize(campo).includes(busca))
              );

          return {
            ...grupo,
            label: grupo.label || tipoLabels[grupo.tipo] || grupo.tipo,
            items,
            valorTotal: items.reduce((total, item) => total + Number(item.valor || 0), 0),
          };
        })
        .filter((grupo) => grupo.items.length > 0);
    },
    totalItensFiltrados() {
      return this.gruposFiltrados.reduce((total, grupo) => total + grupo.items.length, 0);
    },
    summaryCards() {
      const projetos = new Set(this.gruposFiltrados.map((grupo) => this.projectKey(grupo.projeto)));
      const itens = this.gruposFiltrados.reduce((lista, grupo) => lista.concat(grupo.items), []);
      const horas = itens.reduce((total, item) => total + Number(item.horas || 0), 0);
      const valor = itens.reduce((total, item) => total + Number(item.valor || 0), 0);

      return [
        { key: "projetos", label: "Projetos", value: projetos.size, icon: "mdi-folder-multiple-outline", color: "#1c1c44" },
        { key: "itens", label: "Itens consolidados", value: itens.length, icon: "mdi-format-list-checks", color: "#1E88E5" },
        { key: "horas", label: "Horas", value: this.formatNumber(horas), icon: "mdi-clock-outline", color: "#0b5456" },
        { key: "valor", label: "Valor total", value: this.formatMoney(valor), icon: "mdi-currency-usd", color: "#2e7d32" },
      ];
    },
  },
  watch: {
    clientId() {
      this.doLoad();
    },
    anoBase() {
      this.doLoad();
    },
    filtroTipos() {
      this.paineisAbertos = [];
    },
    filtroProjeto() {
      this.paineisAbertos = [];
    },
    busca() {
      this.paineisAbertos = [];
    },
  },
  created() {
    this.doLoad();
  },
  methods: {
    async doLoad() {
      if (!this.clientId || !this.anoBase) return;

      this.limparFiltros();
      this.loading = true;
      try {
        const response = await rdaRpaService.getConsolidado(this.clientId, {
          competenciaIni: `${this.anoBase}-01`,
          competenciaFim: `${this.anoBase}-12`,
        });

        this.consolidado = {
          quantitativo: response.quantitativo || {},
          qualitativo: response.qualitativo || [],
        };
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao carregar dados consolidados para o RPA.",
        });
      } finally {
        this.loading = false;
      }
    },
    limparFiltros() {
      this.filtroTipos = [];
      this.filtroProjeto = null;
      this.busca = "";
      this.paineisAbertos = [];
    },
    normalize(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");
    },
    projectKey(projeto = {}) {
      const codigo = projeto.codigo || projeto.agrupamento_codigo || "";
      const titulo = projeto.titulo || projeto.agrupamento_titulo || "";
      return `${codigo}::${titulo}`;
    },
    formatMoney(value) {
      if (value == null) return "-";
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    projectName(projeto = {}) {
      const code = projeto.codigo || projeto.agrupamento_codigo;
      const title = projeto.titulo || projeto.agrupamento_titulo || "Sem projeto";
      return code ? `${code} - ${title}` : title;
    },
    doConfirmIntegration() {
      this.showConfirm(
        `Tem certeza que deseja realizar a <b>integração com o RDA</b> para a <b>${
          this.selectedClient ? this.selectedClient.fantasia : "empresa selecionada"
        }</b> referente ao ano <b>${this.anoBase}</b>?`,
        () => {
          this.doRPAIntegration();
        }
      );
    },
    doRPAIntegration() {
      this.loading = true;
      rdaRpaService.disparar(this.clientId, {
        plataforma: "novo-sigplani-rda",
        competenciaIni: `${this.anoBase}-01`,
        competenciaFim: `${this.anoBase}-12`,
      })
        .then((ret) => {
          if (!ret.error) {
            this.$notify({
              group: "geral",
              duration: 7000,
              type: "success",
              title: "Preenchimento enviado para fila",
              text: ret.message || "A integração foi enviada para a fila de processamento.",
            });
          }
        })
        .catch((err) => {
          console.error("Erro no disparo do RPA:", err);
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro",
            text: "Erro ao disparar a integração RPA.",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.summary-card {
  border-radius: 8px;
}

.section-title {
  background-color: #f5f5f5;
  color: #1c1c44;
}
</style>
